import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Clock, ExternalLink, MessageCircle, X } from 'lucide-react';
import tenantWebSocketService from '../../services/tenantWebSocketService';
import botAutoResolveService from '../../services/botAutoResolveService';
import soundAlertService from '../../services/soundAlertService';
import { getConversationMessages } from '../../services/chatService';
import { extractMessagesArray, extractPaginationInfo } from '../../lib/state/hooks/chatQueries/transformers';
import { SOUND_CONSTANTS, SOUND_TYPES } from '../../constants/soundConstants';
import { getAvatarColor } from '../chat/components/Avatar';
import { ACTIVE_TABS } from '../chat/types';
import { showError, showSuccess } from '../../utils/toast';

const sortMessagesByCreatedAt = (items) =>
  [...items].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

const normalizeCard = (payload) => {
  if (!payload || payload.card_id == null || payload.conversation_id == null) {
    return null;
  }
  const rawItems = Array.isArray(payload.messages?.items) ? payload.messages.items : [];
  const items = sortMessagesByCreatedAt(rawItems);
  const { hasMore } = extractPaginationInfo(payload.messages || {});
  return {
    cardId: payload.card_id,
    conversationId: payload.conversation_id,
    customerName: payload.customer_name || 'Customer',
    message:
      payload.message ||
      'conversation was resolved by the bot. Please review it.',
    resolveMessage: payload.resolve_message || '',
    expiresAt: payload.expires_at || null,
    messages: items,
    hasMoreMessages: hasMore,
  };
};

const getRemainingSeconds = (expiresAt) => {
  if (!expiresAt) return null;
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(diffMs)) return null;
  return Math.max(0, Math.round(diffMs / 1000));
};

const formatCountdown = (totalSeconds) => {
  if (totalSeconds == null) return '--:--';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const getInitial = (name) => (name || '?').trim().charAt(0).toUpperCase() || '?';

const formatMessageTime = (createdAt) => {
  if (!createdAt) return '';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const MessageBubble = ({ item }) => {
  const isCustomer = item.sender_type === 'Customer';
  const isBot = item.sender_type === 'Bot';
  const time = formatMessageTime(item.created_at);
  const body = item.body || item.caption || (item.media_kind ? `[${item.media_kind}]` : '');

  if (isCustomer) {
    return (
      <div className="flex items-start gap-2.5">
        <div className="shrink-0 mt-0.5">
          {item.sender_profile_pic ? (
            <img
              src={item.sender_profile_pic}
              alt=""
              className="w-7 h-7 rounded-full object-cover"
            />
          ) : (
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${getAvatarColor(
                item.sender_name
              )}`}
            >
              {getInitial(item.sender_name)}
            </div>
          )}
        </div>
        <div className="relative min-w-0 max-w-[80%] bg-gray-100 text-gray-800 text-sm rounded-md px-3 py-2 break-words">
          <span className="absolute -left-[6px] top-3 w-0 h-0 border-y-[6px] border-y-transparent border-r-[6px] border-r-gray-100" />
          <div className="flex items-center justify-between gap-3">
            <span className="text-[12px] font-semibold text-blue-600">
              {item.sender_name || 'Customer'}
            </span>
            {time && <span className="text-[11px] text-gray-400 shrink-0">{time}</span>}
          </div>
          <div className="mt-0.5 text-[13px]">{body}</div>
        </div>
      </div>
    );
  }

  const avatarLabel = item.sender_name || item.sender_type || 'Agent';
  const bubbleBgClass = isBot ? 'bg-blue-50' : 'bg-gray-100';
  const tailBorderClass = isBot ? 'border-l-blue-50' : 'border-l-gray-100';

  return (
    <div className="flex items-start gap-2.5 justify-end">
      <div className={`relative min-w-0 max-w-[80%] ${bubbleBgClass} text-gray-800 text-sm rounded-md px-3 py-2 break-words`}>
        <span
          className={`absolute -right-[5px] top-3 w-0 h-0 border-y-[6px] border-y-transparent border-l-[6px] ${tailBorderClass}`}
        />
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] font-semibold text-blue-600">
            {item.sender_name || (isBot ? 'Bot' : item.sender_type || 'Agent')}
          </span>
          {time && <span className="text-[11px] text-gray-400 shrink-0">{time}</span>}
        </div>
        <div className="mt-0.5 text-[13px]">{body}</div>
      </div>
      <div className="shrink-0 mt-0.5">
        {isBot ? (
          <div className="w-7 h-7 rounded-full bg-[#E0E9FD] flex items-center justify-center">
            <Bot className="w-4 h-4 text-[#2563eb]" />
          </div>
        ) : (
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${getAvatarColor(
              avatarLabel
            )}`}
          >
            {getInitial(avatarLabel)}
          </div>
        )}
      </div>
    </div>
  );
};

const ReviewConversationModal = () => {
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [submittingAction, setSubmittingAction] = useState(null);
  const [previewHasMore, setPreviewHasMore] = useState(false);
  const [previewLoadingMore, setPreviewLoadingMore] = useState(false);
  const cardRef = useRef(null);
  const previewScrollRef = useRef(null);
  const previewPageRef = useRef(1);
  const previewScrollAnchorRef = useRef(null);
  const submittingActionRef = useRef(null);

  const closeModal = useCallback(() => {
    soundAlertService.cancelAllSounds();
    cardRef.current = null;
    setCard(null);
    setRemainingSeconds(null);
    setSubmittingAction(null);
    previewPageRef.current = 1;
    previewScrollAnchorRef.current = null;
    setPreviewHasMore(false);
    setPreviewLoadingMore(false);
  }, []);

  const openModal = useCallback((nextCard) => {
    cardRef.current = nextCard;
    setCard(nextCard);
    setRemainingSeconds(getRemainingSeconds(nextCard.expiresAt));
    setSubmittingAction(null);
    previewPageRef.current = 1;
    previewScrollAnchorRef.current = null;
    setPreviewHasMore(!!nextCard.hasMoreMessages);
    setPreviewLoadingMore(false);
  }, []);

  useEffect(() => {
    submittingActionRef.current = submittingAction;
  }, [submittingAction]);

  // Subscribe to the Tenant WebSocket events for the bot auto-resolve review card.
  useEffect(() => {
    const handleCard = (payload) => {
      const nextCard = normalizeCard(payload);
      if (!nextCard) return;
      openModal(nextCard);
    };

    const handleDismissed = (payload) => {
      const current = cardRef.current;
      if (!current) return;
      const dismissedConversationId = payload?.conversation_id;
      const dismissedCardId = payload?.card_id;
      const matches =
        dismissedConversationId == null && dismissedCardId == null
          ? true
          : (dismissedConversationId != null &&
              String(dismissedConversationId) === String(current.conversationId)) ||
            (dismissedCardId != null && String(dismissedCardId) === String(current.cardId));
      if (matches) {
        closeModal();
      }
    };

    tenantWebSocketService.on('conversation.bot_auto_resolve_card', handleCard);
    tenantWebSocketService.on('conversation.bot_auto_resolve_card_dismissed', handleDismissed);

    return () => {
      tenantWebSocketService.off('conversation.bot_auto_resolve_card', handleCard);
      tenantWebSocketService.off('conversation.bot_auto_resolve_card_dismissed', handleDismissed);
    };
  }, [openModal, closeModal]);

  // Countdown timer — recomputed from expires_at every tick so it stays accurate.
  useEffect(() => {
    if (!card?.expiresAt) return undefined;

    const tick = () => {
      const remaining = getRemainingSeconds(card.expiresAt);
      setRemainingSeconds(remaining);
      if (remaining === 0) {
        closeModal();
      }
    };

    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [card, closeModal]);

  // Scroll the Conversation Preview to the bottom (latest message) whenever a new card opens.
  useEffect(() => {
    if (!card) return;
    const el = previewScrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [card?.cardId]);

  // Fetch the first page of messages directly from the API using the conversation_id
  // from the socket payload, so the preview reflects the server's paginated state
  // (not just whatever the socket happened to embed) — same source as the /chat screen.
  useEffect(() => {
    if (!card) return undefined;

    let cancelled = false;
    const { cardId, conversationId } = card;

    (async () => {
      try {
        const response = await getConversationMessages(conversationId, 1);
        if (cancelled) return;

        const items = sortMessagesByCreatedAt(extractMessagesArray(response));
        const { hasMore } = extractPaginationInfo(response);

        previewPageRef.current = 1;
        setPreviewHasMore(hasMore);
        setCard((prev) => (prev && prev.cardId === cardId ? { ...prev, messages: items } : prev));

        setTimeout(() => {
          const el = previewScrollRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        }, 0);
      } catch (error) {
        // Keep whatever messages the socket payload already provided.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [card?.cardId]);

  // After older messages are prepended (loading more on scroll-to-top), restore the
  // scroll offset so the view doesn't jump — same technique as the /chat message list.
  useEffect(() => {
    const anchor = previewScrollAnchorRef.current;
    if (!anchor) return;
    const el = previewScrollRef.current;
    if (!el) return;

    const timeoutId = setTimeout(() => {
      const heightDifference = el.scrollHeight - anchor.scrollHeight;
      el.scrollTop = anchor.scrollTop + heightDifference;
      previewScrollAnchorRef.current = null;
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [card?.messages?.length]);

  // Alarm: play a 2s tone immediately, then repeat every 5s until the agent
  // takes action (accept/resolve) or the card is closed/expired/dismissed.
  useEffect(() => {
    if (!card) return undefined;

    const playAlarm = () => {
      if (submittingActionRef.current) return;
      soundAlertService.playNotificationSound(SOUND_TYPES.REVIEW_ALARM);
    };

    playAlarm();
    const intervalId = setInterval(playAlarm, SOUND_CONSTANTS.REVIEW_ALARM_REPEAT_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [card?.cardId]);

  // Infinite scroll: when the user scrolls to the top of the currently loaded
  // messages, fetch the next (older) page for this conversation and prepend it —
  // same "scroll to top to load more" behavior as the /chat conversation screen.
  const handlePreviewScroll = useCallback(async () => {
    const el = previewScrollRef.current;
    if (!el || !card || previewLoadingMore || !previewHasMore) return;

    // Only treat this as "scrolled to top" when the list actually overflows —
    // otherwise scrollTop is always 0 and this fires on mount, spuriously
    // loading + re-sorting messages before the user ever scrolls.
    const hasOverflow = el.scrollHeight > el.clientHeight;
    const isAtTop = hasOverflow && el.scrollTop <= 50;
    if (!isAtTop) return;

    const conversationId = card.conversationId;
    const nextPage = previewPageRef.current + 1;

    previewScrollAnchorRef.current = {
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight,
    };
    setPreviewLoadingMore(true);
    try {
      const response = await getConversationMessages(conversationId, nextPage);
      const newItems = extractMessagesArray(response);
      const { hasMore } = extractPaginationInfo(response);

      previewPageRef.current = nextPage;
      setPreviewHasMore(hasMore);
      if (newItems.length > 0) {
        setCard((prev) => {
          if (!prev) return prev;
          const merged = [...newItems, ...prev.messages].sort(
            (a, b) => new Date(a.created_at) - new Date(b.created_at)
          );
          return { ...prev, messages: merged };
        });
      } else {
        previewScrollAnchorRef.current = null;
      }
    } catch (error) {
      setPreviewHasMore(false);
      previewScrollAnchorRef.current = null;
    } finally {
      setPreviewLoadingMore(false);
    }
  }, [card, previewHasMore, previewLoadingMore]);

  const handleRespond = useCallback(
    async (action) => {
      if (submittingAction || !card) return;

      soundAlertService.cancelAllSounds();
      setSubmittingAction(action);
      try {
        const result = await botAutoResolveService.respondToBotAutoResolveCard(
          card.conversationId,
          { cardId: card.cardId, action }
        );

        if (result.isSuccess) {
          showSuccess(
            action === 'accept'
              ? 'You have taken ownership of this conversation.'
              : 'Conversation resolved successfully.'
          );

          if (action === 'accept') {
            const targetConversationId = card.conversationId;
            navigate('/chat');
            // Small delay so the Chat page has mounted and registered its
            // 'navigate_to_conversation' listener before we dispatch it.
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent('navigate_to_conversation', {
                  detail: { conversationId: Number(targetConversationId), tabName: ACTIVE_TABS.MY },
                })
              );
            }, 300);
          }

          closeModal();
        } else {
          showError(result.error?.message || 'Failed to submit your response. Please try again.');
          setSubmittingAction(null);
        }
      } catch (error) {
        showError('Failed to submit your response. Please try again.');
        setSubmittingAction(null);
      }
    },
    [submittingAction, card, closeModal, navigate]
  );

  const handleViewFullConversation = useCallback(() => {
    navigate('/chat');
  }, [navigate]);

  if (!card) return null;

  const isSubmitting = submittingAction !== null;
  const hasTimer = card.expiresAt != null && remainingSeconds != null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[100000]" />

      {/* Centered modal */}
      <div className="fixed inset-0 z-[100001] flex items-center justify-center p-3 sm:p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[28rem] max-h-[90vh] flex flex-col overflow-hidden">

          {/* Scrollable body — kicks in with a thin gray scrollbar when the
              viewport is too short to fit everything at once. */}
          <div className="review-conversation-modal-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-5 pt-6 sm:pt-8 pb-2 text-center [@media(max-height:720px)]:pt-3 [@media(max-height:720px)]:pb-1">
            {/* Bot icon */}
            <div className="mx-auto mb-2 -mt-2 sm:-mt-4 flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-[#E0E9FD] [@media(max-height:720px)]:mb-1 [@media(max-height:720px)]:-mt-1 [@media(max-height:720px)]:h-9 [@media(max-height:720px)]:w-9">
              <Bot className="h-6 w-6 sm:h-7 sm:w-7 text-[#2563eb] [@media(max-height:720px)]:h-5 [@media(max-height:720px)]:w-5" />
            </div>

            <h2 className="text-lg sm:text-xl font-bold text-gray-900 [@media(max-height:720px)]:text-base">Review Conversation</h2>

            {/* Customer name (blue) + message from socket */}
            <p className="mt-3 text-[13px] leading-relaxed text-gray-800 w-full max-w-[20rem] mx-auto [@media(max-height:720px)]:mt-1.5">
              <span className="font-semibold text-blue-600 ">{card.customerName}</span>{' '}
              {card.message}
            </p>

            {/* Conversation Preview */}
            <div className="mt-5 rounded-xl border border-gray-200 text-left overflow-hidden [@media(max-height:720px)]:mt-2.5">
              <div className="flex items-center justify-between px-4 py-3 pb-0 [@media(max-height:720px)]:py-2">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-blue-600" />
                  <span className="text-[13px] font-semibold text-gray-900">Conversation Preview</span>
                </div>
              </div>
              <div
                ref={previewScrollRef}
                onScroll={handlePreviewScroll}
                className="review-conversation-preview-scroll max-h-[38vh] sm:max-h-[330px] overflow-y-auto overflow-x-hidden px-4 py-4 space-y-4 [@media(max-height:720px)]:max-h-[160px] [@media(max-height:720px)]:py-2 [@media(max-height:720px)]:space-y-2"
                style={{
                  backgroundImage: 'url(/images/with%20background.svg)',
                  backgroundRepeat: 'repeat',
                  backgroundSize: '800px',
                  backgroundPosition: 'center',
                }}
              >
                {previewLoadingMore && (
                  <p className="text-xs text-gray-400 text-center py-2">Loading more messages...</p>
                )}
                {card.messages.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No messages to preview.</p>
                ) : (
                  card.messages.map((item) => <MessageBubble key={item.id} item={item} />)
                )}
              </div>
            </div>

            {/* Auto-close countdown — moves inline next to the footer heading on short screens */}
            {hasTimer && (
              <p className="pt-[1rem] flex items-center gap-1.5 text-xs text-gray-400 [@media(max-height:720px)]:hidden">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="text-gray-800 text-[12px] font-semibold">Review expires in</span> <span className="font-semibold text-blue-600 text-md">{formatCountdown(remainingSeconds)}</span>
              </p>
            )}

            <div className="mt-[1rem] pb-2 [@media(max-height:720px)]:mt-2 [@media(max-height:720px)]:pb-0" />
          </div>

          {/* Fixed footer — stays visible without needing to scroll the body */}
          <div className="shrink-0 border-t border-gray-200 bg-white px-4 sm:px-5 py-3 sm:py-4 [@media(max-height:720px)]:py-2">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[13px] font-bold text-gray-900">Was the issue resolved?</p>
              {hasTimer && (
                <p className="hidden [@media(max-height:720px)]:flex items-center gap-1 text-xs text-gray-400 shrink-0">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span className="text-gray-800 text-[11px] font-semibold">Expires in</span>
                  <span className="font-semibold text-blue-600">{formatCountdown(remainingSeconds)}</span>
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="mt-3 flex gap-2.5 sm:gap-3 [@media(max-height:720px)]:mt-2">
              <button
                type="button"
                onClick={() => handleRespond('accept')}
                disabled={isSubmitting}
                className="flex-1 rounded-lg border border-blue-200 bg-white px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submittingAction === 'accept' ? 'Please wait...' : 'Accept Chat'}
              </button>
              <button
                type="button"
                onClick={() => handleRespond('resolve')}
                disabled={isSubmitting}
                className="flex-1 rounded-lg bg-blue-600 px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submittingAction === 'resolve' ? 'Resolving...' : 'Confirm Resolved'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReviewConversationModal;
